import { TableRow } from "@carbon6/c6-frontend-design";
import { metricMean, type MetricUnit, type TableHotspotResp } from "@/api";
import { compareNumericNullLast } from "@/components/table/compareNumeric";
import { formatCompactCount } from "@/utils";
import { parseUtcMs } from "@/utils/datetime";
import {
  EMPTY_TOKEN,
  describeMetricUnit,
  formatBytesValue,
  formatCount,
  formatOptionalAt,
  formatRatio,
  type MetricUnitView,
} from "./observabilityModel";

const COMPACT = { compact: true } as const;

/** 정렬용 원시값을 따로 가지는 열이다. 나머지(테이블 이름)는 c6 기본 문자열 비교로 충분하다. */
const NUMERIC_COLUMNS = [
  "blockAccess",
  "seqScan",
  "indexScan",
  "heapBytes",
  "indexBytes",
  "unusedIndexes",
  "rowWrites",
  "propagatedUpdates",
  "statsLag",
  "lastAnalyzeAt",
] as const;
type NumericColumn = (typeof NUMERIC_COLUMNS)[number];

function isNumericColumn(key: string): key is NumericColumn {
  return (NUMERIC_COLUMNS as readonly string[]).includes(key);
}

/** 창 합·마지막 tick 칸이다. 창 요약 칸과 모양이 같아서 같은 셀이 그린다. 봉우리 시각은 없다. */
function composite(value: string, hint = ""): MetricUnitView {
  return { value, hint, peakAt: "" };
}

/** 값이 없는 칸이다. 첫 사이클과 통계 리셋 때의 창 합은 0 이 아니라 미상이다. */
const ABSENT: MetricUnitView = composite(EMPTY_TOKEN);

/** 보조 줄의 수치다. 자리가 좁아서 K/M/B 로 줄인다. 큰 수치(값)는 전체 자릿수를 그대로 둔다. */
const formatHintCount = (value: number | null): string =>
  value === null ? EMPTY_TOKEN : formatCompactCount(value, 1);

/** 스캔 칸이다. 횟수가 값이고, 그 스캔이 읽은 행 수가 보조다. 횟수가 없으면 보조도 비운다. */
export function describeScan(
  count: number | null,
  rows: number | null,
  rowsLabel: string,
): MetricUnitView {
  if (count === null) {
    return ABSENT;
  }
  return composite(
    formatCount(count),
    rows === null ? "" : `${rowsLabel} ${formatHintCount(rows)}`,
  );
}

/** 블록 접근 칸이다. 합이 값이고, 디스크 읽기와 적중률이 보조다. */
export function describeBlockAccess(
  hotspot: Pick<TableHotspotResp, "block_accesses" | "block_reads" | "cache_hit_ratio">,
): MetricUnitView {
  if (hotspot.block_accesses === null) {
    return ABSENT;
  }
  const parts: string[] = [];
  if (hotspot.block_reads !== null) {
    parts.push(`디스크 ${formatHintCount(hotspot.block_reads)}`);
  }
  // 분모가 0 이면 서버가 값을 비운다. 접근이 0 인 창의 적중률은 "100%" 도 "0%" 도 아니다.
  if (hotspot.cache_hit_ratio !== null) {
    parts.push(`적중 ${formatRatio(hotspot.cache_hit_ratio)}`);
  }
  return composite(formatCount(hotspot.block_accesses), parts.join(" · "));
}

/** 행 쓰기 칸이다. 합이 값이고, 삽입·갱신·삭제가 보조다. */
export function describeRowWrites(
  hotspot: Pick<
    TableHotspotResp,
    "rows_written" | "rows_inserted" | "rows_updated" | "rows_deleted"
  >,
): MetricUnitView {
  if (hotspot.rows_written === null) {
    return ABSENT;
  }
  return composite(
    formatCount(hotspot.rows_written),
    `삽입 ${formatHintCount(hotspot.rows_inserted)} · 갱신 ${formatHintCount(hotspot.rows_updated)} · 삭제 ${formatHintCount(hotspot.rows_deleted)}`,
  );
}

/** 인덱스 전파 갱신 칸이다. non-HOT 비율이 값이고, 갱신 건수가 보조다. 갱신이 0 건이면 서버가 비율을 비운다. */
export function describePropagatedUpdates(
  hotspot: Pick<TableHotspotResp, "non_hot_update_ratio" | "rows_updated">,
): MetricUnitView {
  if (hotspot.non_hot_update_ratio === null) {
    return ABSENT;
  }
  return composite(
    formatRatio(hotspot.non_hot_update_ratio),
    `갱신 ${formatHintCount(hotspot.rows_updated)}건 중`,
  );
}

/**
 * 미사용 인덱스 칸이다. 개수·크기가 값이고, 전체 인덱스 수가 보조다.
 *
 * `null`(아직 세지 않음)과 `0`(미사용 없음)을 다른 문구로 표시한다. `null` 을 "없음" 으로 표시하면
 * 첫 tick 에 모든 테이블이 문제없어 보인다.
 */
export function describeUnusedIndexes(
  hotspot: Pick<TableHotspotResp, "index_count" | "unused_index_count" | "unused_index_bytes">,
): MetricUnitView {
  if (hotspot.unused_index_count === null) {
    return ABSENT;
  }
  const total =
    hotspot.index_count === null ? "" : `인덱스 ${formatCount(hotspot.index_count)}개 중`;
  if (hotspot.unused_index_count === 0) {
    return composite("없음", total);
  }
  const bytes =
    hotspot.unused_index_bytes === null ? "" : ` · ${formatBytesValue(hotspot.unused_index_bytes)}`;
  return composite(`${formatCount(hotspot.unused_index_count)}개${bytes}`, total);
}

/** 행 수다. 테이블 이름 아래의 보조 한 줄이다. 표본이 없으면 줄을 만들지 않는다. */
export function describeRowCount(unit: MetricUnit): string {
  const mean = metricMean(unit);
  return mean !== null ? `${formatCount(mean)}행` : "";
}

/**
 * 테이블 hotspot 한 행이다.
 *
 * 값이 세 종류(창 요약·창 합·마지막 tick)라서 칸마다 describer 가 다르다. 그러나 모든 칸은 같은
 * 모양(큰 수치와 보조 한 줄)이라서 열이 같은 셀로 그린다. 정렬은 칸 문자열이 아니라 원시값으로 한다.
 */
export class TableHotspotTableRow extends TableRow {
  readonly qualifiedName: string;
  readonly rowCount: string;
  readonly blockAccess: MetricUnitView;
  readonly seqScan: MetricUnitView;
  readonly indexScan: MetricUnitView;
  readonly heapBytes: MetricUnitView;
  readonly indexBytes: MetricUnitView;
  readonly unusedIndexes: MetricUnitView;
  readonly unusedIndexNames: readonly string[];
  readonly rowWrites: MetricUnitView;
  readonly propagatedUpdates: MetricUnitView;
  readonly statsLag: MetricUnitView;
  readonly lastAnalyzeAt: string;

  // 정렬용 원시값이다. 창 요약은 평균, 미사용 인덱스는 바이트, 시각은 epoch ms 를 쓴다.
  private readonly sortValues: Readonly<Record<NumericColumn, number | null>>;

  constructor(hotspot: TableHotspotResp) {
    super(null);
    this.qualifiedName = `${hotspot.schema_name}.${hotspot.table_name}`;
    this.rowCount = describeRowCount(hotspot.live_tuples);
    this.blockAccess = describeBlockAccess(hotspot);
    this.seqScan = describeScan(hotspot.sequential_scans, hotspot.sequential_rows_read, "읽은 행");
    this.indexScan = describeScan(hotspot.index_scans, hotspot.index_rows_fetched, "가져온 행");
    // 표 칸은 봉우리 시각을 빼고 툴팁으로 보낸다. 넣으면 열 폭 때문에 잘려 산포까지 사라진다.
    this.heapBytes = describeMetricUnit(hotspot.heap_bytes, formatBytesValue, COMPACT);
    this.indexBytes = describeMetricUnit(hotspot.index_bytes, formatBytesValue, COMPACT);
    this.unusedIndexes = describeUnusedIndexes(hotspot);
    this.unusedIndexNames = hotspot.unused_index_names;
    this.rowWrites = describeRowWrites(hotspot);
    this.propagatedUpdates = describePropagatedUpdates(hotspot);
    this.statsLag = describeMetricUnit(hotspot.rows_since_analyze, formatCount, COMPACT);
    this.lastAnalyzeAt = formatOptionalAt(hotspot.last_autoanalyze_at);
    this.sortValues = {
      blockAccess: hotspot.block_accesses,
      seqScan: hotspot.sequential_scans,
      indexScan: hotspot.index_scans,
      heapBytes: metricMean(hotspot.heap_bytes),
      indexBytes: metricMean(hotspot.index_bytes),
      unusedIndexes: hotspot.unused_index_bytes,
      rowWrites: hotspot.rows_written,
      propagatedUpdates: hotspot.non_hot_update_ratio,
      statsLag: metricMean(hotspot.rows_since_analyze),
      lastAnalyzeAt: parseUtcMs(hotspot.last_autoanalyze_at),
    };
  }

  getRowId(): string {
    return this.qualifiedName;
  }

  // 오름차순 기준이다. 내림차순은 DataTable 이 부호를 바꾼다.
  override compare(other: this, columnKey: string): number {
    if (isNumericColumn(columnKey)) {
      return compareNumericNullLast(this.sortValues[columnKey], other.sortValues[columnKey]);
    }
    return super.compare(other, columnKey);
  }

  // 서버가 이미 크기 상위 20개로 줄였다. 화면은 더 거르지 않는다.
  canShow(): boolean {
    return true;
  }

  // 읽기 전용 목록이라 선택 기능이 없다.
  canSelect(): boolean {
    return false;
  }
}

export function createTableHotspotRows(
  hotspots: readonly TableHotspotResp[],
): TableHotspotTableRow[] {
  return hotspots.map((hotspot) => new TableHotspotTableRow(hotspot));
}
